import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { useCallback, useEffect, useState } from 'react';
import { ActionsHeader } from '../../components/ActionsHeader';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTable from '@nxsys/components/AppTableContainer';
import AppCard from '@nxsys/components/AppCard';
import { Dropdown, Modal } from 'antd';
import AppButton from '@nxsys/components/AppButton';
import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import { getColumns } from './columns';
import {
  DeleteStatutoryParams,
  GetStatutoryLeaveListParams,
} from '@nxsys/store/slices/statutory/types';
import { useDispatch } from 'react-redux';
import { AppDispatch, AppState } from '@nxsys/store';
import { statutoryActions } from '@nxsys/store/slices/statutory/reducer/statutorySlice';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import { useSelector } from 'react-redux';
import {
  STATUTORY_PAYMENT_TYPE,
  STATUTORY_TYPE,
} from '@nxsys/constants/statutory';
import CreateStatutoryModal from '../../components/CreateStatutoryModal';
import { deleteStatutory } from '@nxsys/store/slices/statutory/action';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';

const StatutoryLeave = () => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const taxYear = getTaxWeekTaxYear().taxYear;
  const { statutoryLeaveData, loading } = useSelector(
    (state: AppState) => state.statutoryStore,
  );
  const { list, pagination, queryParams } = statutoryLeaveData;
  const { isBureauEmployer, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const navigate = useNavigateWithQuery();

  const [createStatutoryModal, setCreateStatutoryModal] = useState<{
    open: boolean;
    title: string;
    type: (typeof STATUTORY_PAYMENT_TYPE)[keyof typeof STATUTORY_PAYMENT_TYPE];
  }>({
    open: false,
    type: STATUTORY_PAYMENT_TYPE.SSP,
    title: '',
  });

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['statutory.leave.title']}
        separator=""
        title={''}
      />
    );
  }, [messages]);

  const renderAddNewBtn = (variant = 'solid') => {
    const menuProps = {
      items: [
        {
          key: '1',
          label: messages['statutory.detail.sick'],
          onClick: () =>
            setCreateStatutoryModal({
              open: true,
              type: STATUTORY_PAYMENT_TYPE.SSP,
              title: messages['statutory.detail.addNewSSP'],
            }),
        },
        {
          key: '2',
          label: messages['statutory.detail.maternity'],
          onClick: () =>
            setCreateStatutoryModal({
              open: true,
              type: STATUTORY_PAYMENT_TYPE.SMP,
              title: messages['statutory.detail.addNewSMP'],
            }),
        },
        {
          key: '3',
          label: messages['statutory.detail.adoption'],
          onClick: () =>
            setCreateStatutoryModal({
              open: true,
              type: STATUTORY_PAYMENT_TYPE.SAP,
              title: messages['statutory.detail.addNewSAP'],
            }),
        },
        {
          key: '4',
          label: messages['statutory.detail.paternityAdoption'],
          onClick: () =>
            setCreateStatutoryModal({
              open: true,
              type: STATUTORY_PAYMENT_TYPE.SPP_ADOPTION,
              title: messages['statutory.detail.addNewSPPA'],
            }),
        },
        {
          key: '5',
          label: messages['statutory.detail.paternityChildbirth'],
          onClick: () =>
            setCreateStatutoryModal({
              open: true,
              type: STATUTORY_PAYMENT_TYPE.SPP_BIRTH,
              title: messages['statutory.detail.addNewSPPB'],
            }),
        },
      ],
    };

    return (
      <Dropdown menu={menuProps}>
        <AppButton
          type="primary"
          name={messages['common.addNew']}
          leftIcon={<PlusOutlined />}
          rightIcon={<DownOutlined />}
          variant={variant as any}
        />
      </Dropdown>
    );
  };

  const renderRightContent = useCallback(() => {
    return <div className="flex items-center gap-2">{renderAddNewBtn()}</div>;
  }, [renderAddNewBtn]);

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear,
    });
  }, []);

  const handleFetchData = (params: GetStatutoryLeaveListParams) => {
    const newParams = { ...params };

    newParams.companyIds = resolveFilterCompanyIds(
      newParams.companyIds,
      isBureauEmployer,
      user?.companyUserIds,
    );
    dispatch(statutoryActions.getStatutoryLeaveRecords(newParams));
  };

  const refetchData = () => handleFetchData({ ...queryParams });

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const toggleCreateStatutory = () =>
    setCreateStatutoryModal({
      ...createStatutoryModal,
      open: !createStatutoryModal.open,
    });

  const handleDeleteStatutory = async (params: DeleteStatutoryParams) => {
    Modal.confirm({
      title: messages['statutory.confirmToDelete'],
      content: messages['statutory.areYouSureToDelete'],
      onOk: async () => {
        await dispatch(deleteStatutory(params));
        refetchData();
      },
    });
  };

  const handleViewDetail = (type: string, id: string) => {
    navigate(`/statutory-leave/${type}/${id}`);
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.status ||
    !!queryParams?.taxYear ||
    !!queryParams?.companyIds ||
    !!queryParams?.agencyIds ||
    !!queryParams?.type ||
    !!queryParams?.payFrequency;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>
        <AppsHeader>
          <ActionsHeader
            onFilter={handleFetchData}
            type={STATUTORY_TYPE.LEAVE}
            fetchData={handleFetchData}
          />
        </AppsHeader>
        <AppsContent>
          <AppTable
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 2500, y: 700 }}
            titleEmpty={messages['statutory.leave.empty']}
            columns={getColumns({
              handleDeleteStatutory,
              messages,
              handleViewDetail,
            })}
            onChangePagination={onChangePagination}
            noDataType={isFiltering ? 'noDataFound' : 'noDataCreated'}
            addNewBtnName={messages['common.addNew']}
            addNewBtn={renderAddNewBtn('outlined')}
          />
        </AppsContent>
      </AppCard>
      {createStatutoryModal.open && (
        <CreateStatutoryModal
          isOpen={createStatutoryModal.open}
          title={createStatutoryModal.title}
          type={createStatutoryModal.type}
          toggle={toggleCreateStatutory}
          refetchData={refetchData}
        />
      )}
    </AppsContainer>
  );
};

export default StatutoryLeave;
